import { useEffect, useState, useCallback } from "react";
import "./InteriorGallery.css";

/**
 * Props:
 * - title: string (ex: "INTÉRIEUR")
 * - images: string[] (jet.images?.interiors)
 * - layout: string | null (jet.images?.layout)
 * - starShift: string CSS (ex: "-3%") pour décaler la compo diamant
 */
export default function InteriorGallery({
  title = "INTÉRIEUR",
  images = [],
  layout = null,
  starShift = "-3%",
}) {
  const pics = (images || []).filter(Boolean);
  const hasPics = pics.length > 0;
  const hasLayout = Boolean(layout);

  // ---- Lightbox (valable pour layout + photos)
  // On met toutes les sources dans un seul tableau pour naviguer
  const all = [
    ...(hasLayout ? [{ src: layout, kind: "layout" }] : []),
    ...pics.map((src) => ({ src, kind: "pic" })),
  ];
  const [isOpen, setIsOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  const openAt = useCallback((i) => { setIdx(i); setIsOpen(true); }, []);
  const close  = useCallback(() => setIsOpen(false), []);
  const next   = useCallback(() => setIdx((i) => (i + 1) % (all.length || 1)), [all.length]);
  const prev   = useCallback(() => setIdx((i) => (i - 1 + (all.length || 1)) % (all.length || 1)), [all.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, close, next, prev]);

  // Prépare la compo diamant (1 centrale + 6 autour max)
  const star = pics.slice(0, 7);
  const rest = pics.slice(7);

  const onKeyOpen = (i) => (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openAt(i); }
  };

  // ---- Render ----
  if (!hasPics && !hasLayout) return null;

  return (
    <section className="int" style={{ ["--star-shift"]: starShift }}>
      <h3 className="int-title">{title}</h3>

      <div className="int-2col">
        {/* Colonne gauche: Layout */}
        {hasLayout ? (
          <aside className="lg-layout">
            <h4 className="lg-subtitle">PLAN CABINE</h4>
            <div
              className="lg-card"
              role="button"
              tabIndex={0}
              onClick={() => openAt(0)}                 // index 0 = layout dans 'all'
              onKeyDown={onKeyOpen(0)}
              aria-label="Agrandir le plan cabine"
            >
              <img src={layout} alt="Plan cabine / layout" loading="lazy" />
            </div>
            <p className="lg-hint">Cliquez pour agrandir</p>
          </aside>
        ) : (
          <div aria-hidden className="lg-placeholder" />
        )}

        {/* Séparateur */}
        <div className="lg-sep" aria-hidden />

        {/* Colonne droite: Galerie diamant */}
        <div className="lg-gallery">
          {hasPics ? (
            <>
              <div className="int-star">
                {star[0] && (
                  <figure
                    className="dia dia--xl"
                    role="button"
                    tabIndex={0}
                    onClick={() => openAt(hasLayout ? 1 : 0)}
                    onKeyDown={onKeyOpen(hasLayout ? 1 : 0)}
                  >
                    <img src={star[0]} alt="Intérieur – principal" loading="lazy" />
                  </figure>
                )}
                {star[1] && (
                  <figure className="dia dia--n" role="button" tabIndex={0}
                          onClick={() => openAt((hasLayout ? 1 : 0) + 1)}
                          onKeyDown={onKeyOpen((hasLayout ? 1 : 0) + 1)}>
                    <img src={star[1]} alt="Intérieur" loading="lazy" />
                  </figure>
                )}
                {star[2] && (
                  <figure className="dia dia--ne" role="button" tabIndex={0}
                          onClick={() => openAt((hasLayout ? 1 : 0) + 2)}
                          onKeyDown={onKeyOpen((hasLayout ? 1 : 0) + 2)}>
                    <img src={star[2]} alt="Intérieur" loading="lazy" />
                  </figure>
                )}
                {star[3] && (
                  <figure className="dia dia--e" role="button" tabIndex={0}
                          onClick={() => openAt((hasLayout ? 1 : 0) + 3)}
                          onKeyDown={onKeyOpen((hasLayout ? 1 : 0) + 3)}>
                    <img src={star[3]} alt="Intérieur" loading="lazy" />
                  </figure>
                )}
                {star[4] && (
                  <figure className="dia dia--se" role="button" tabIndex={0}
                          onClick={() => openAt((hasLayout ? 1 : 0) + 4)}
                          onKeyDown={onKeyOpen((hasLayout ? 1 : 0) + 4)}>
                    <img src={star[4]} alt="Intérieur" loading="lazy" />
                  </figure>
                )}
                {star[5] && (
                  <figure className="dia dia--s" role="button" tabIndex={0}
                          onClick={() => openAt((hasLayout ? 1 : 0) + 5)}
                          onKeyDown={onKeyOpen((hasLayout ? 1 : 0) + 5)}>
                    <img src={star[5]} alt="Intérieur" loading="lazy" />
                  </figure>
                )}
                {star[6] && (
                  <figure className="dia dia--sw" role="button" tabIndex={0}
                          onClick={() => openAt((hasLayout ? 1 : 0) + 6)}
                          onKeyDown={onKeyOpen((hasLayout ? 1 : 0) + 6)}>
                    <img src={star[6]} alt="Intérieur" loading="lazy" />
                  </figure>
                )}
              </div>

              {rest.length > 0 && (
                <div className="int-grid">
                  {rest.map((src, i) => {
                    const base = (hasLayout ? 1 : 0) + star.length;
                    const targetIdx = base + i;
                    return (
                      <figure
                        className="dia"
                        key={i}
                        role="button"
                        tabIndex={0}
                        onClick={() => openAt(targetIdx)}
                        onKeyDown={onKeyOpen(targetIdx)}
                      >
                        <img src={src} alt={`Intérieur ${star.length + i + 1}`} loading="lazy" />
                      </figure>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <p className="int-empty">Aucune photo intérieure disponible.</p>
          )}
        </div>
      </div>

      {/* LIGHTBOX commune (layout + photos) */}
      {isOpen && (
        <div className="lb" onClick={close} role="dialog" aria-modal="true">
          <button className="lb-close" aria-label="Fermer"
                  onClick={(e) => { e.stopPropagation(); close(); }}>×</button>
          <button className="lb-nav lb-prev" aria-label="Précédente"
                  onClick={(e) => { e.stopPropagation(); prev(); }}>‹</button>
          <img
            className="lb-img"
            src={all[idx].src}
            alt={all[idx].kind === "layout" ? "Plan cabine (agrandi)" : `Intérieur ${idx + 1}`}
            onClick={(e) => e.stopPropagation()}
          />
          <button className="lb-nav lb-next" aria-label="Suivante"
                  onClick={(e) => { e.stopPropagation(); next(); }}>›</button>
        </div>
      )}
    </section>
  );
}
